/**
 * Image Compression Utility
 *
 * Resizes and compresses images to optimize for AI processing.
 * Target: 1024x1024 max dimension while maintaining aspect ratio.
 */

const MAX_DIMENSION = 1024;
const JPEG_QUALITY = 0.85;

import { convertBase64ToBlob } from "./media.utils";

export function compressImage(
  imageDataUrl: string,
  maxDimension: number = MAX_DIMENSION,
  quality: number = JPEG_QUALITY,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      let { naturalWidth: width, naturalHeight: height } = img;

      if (width <= maxDimension && height <= maxDimension) {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", quality));
        return;
      }

      const aspectRatio = width / height;
      if (width > height) {
        width = maxDimension;
        height = Math.round(maxDimension / aspectRatio);
      } else {
        height = maxDimension;
        width = Math.round(maxDimension * aspectRatio);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, width, height);

      const compressedDataUrl = canvas.toDataURL("image/jpeg", quality);
      resolve(compressedDataUrl);
    };

    img.onerror = () => {
      reject(new Error("Failed to load image for compression"));
    };

    img.src = imageDataUrl;
  });
}

export function createDownscaledImageIfNeeded(
  imageDataUrl: string,
  maxDimension: number = MAX_DIMENSION,
  quality: number = JPEG_QUALITY,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      let { naturalWidth: width, naturalHeight: height } = img;

      if (width <= maxDimension && height <= maxDimension) {
        resolve(imageDataUrl);
        return;
      }

      const aspectRatio = width / height;
      if (width > height) {
        width = maxDimension;
        height = Math.round(maxDimension / aspectRatio);
      } else {
        height = maxDimension;
        width = Math.round(maxDimension * aspectRatio);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, width, height);

      resolve(canvas.toDataURL("image/jpeg", quality));
    };

    img.onerror = () => {
      reject(new Error("Failed to load image for downscaling"));
    };

    img.src = imageDataUrl;
  });
}

export function getDataUrlSize(dataUrl: string): number {
  const base64 = dataUrl.split(",")[1] || dataUrl;
  return Math.round((base64.length * 3) / 4);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export async function compressImageFile(
  file: File,
  maxDimension: number = MAX_DIMENSION,
  quality: number = JPEG_QUALITY,
): Promise<File> {
  const sourceDataUrl = await readFileAsDataUrl(file);
  const compressedDataUrl = await compressImage(sourceDataUrl, maxDimension, quality);
  const blob = convertBase64ToBlob(compressedDataUrl);
  return new File([blob], buildCompressedFileName(file.name), {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Failed to read file for compression"));

    reader.readAsDataURL(file);
  });
}

function buildCompressedFileName(originalName: string): string {
  const nameWithoutExtension = originalName.replace(/\.[^.]+$/, "") || "compressed";
  return `${nameWithoutExtension}.jpg`;
}
